/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { arrayByLength, uniq } from "../../../../../shared";
import { last, minBy } from "lodash";
import { TableProps } from "../../../components/Table";
import { ColDef } from "ag-grid-community";
import { SLIDE_NUMBER, UNIT_USD_BU } from "../constants";

const COLUMNS_AXIS = "calendar.default";
const ROWS_AXIS = "commodities.default";
const UNITS_AXIS = "units.default";

export function useAgGridTransformer(olapData: OLAPTransformerResult | null): TableProps | undefined {
  const [state, setState] = useState<TableProps | undefined>();

  const [calendar] = useParameter<number[]>('@calendar');
  const [commodities] = useParameter<number[]>('@commodities');
  const [units] = useParameter<number[]>('@units');

  const [columnsDictItems] = useDictionary(dictionaryService, "slides-columns-slide12");
  const columnsIds = ['current_price', "last_quarter_value", "last_year_quarter_value", "five_year_avg", "five_year_high", "five_year_low"];
  const columns = useMemo(() => columnsDictItems?.map((item, index) => ({
    headerName: item.getValue<any>('name')[9] || '',
    field: columnsIds[index],
    resizable: true,
  })) || [], [columnsDictItems]);

  useEffect(() => {
    if (olapData) {
      const rows = commodities?.flatMap((commodity) => {
        // a commodity may have rows in several units, take the one that comes first in @units
        const commodityRows = olapData?.rows.filter(row => Number(row.selection[ROWS_AXIS]) === commodity) || [];
        const dataRow = minBy(commodityRows, row => {
          const unitIndex = units?.indexOf(Number(row.selection[UNITS_AXIS])) ?? -1;
          return unitIndex === -1 ? Infinity : unitIndex;
        });
        let rowName = dataRow?.attributes[`${ROWS_AXIS}.name`][9] || '';
        let rowComment = dataRow?.attributes[`${ROWS_AXIS}.comment`] || '';
        const isUsdBu = Number(dataRow?.selection[UNITS_AXIS]) === UNIT_USD_BU;
        // comment holds the unit, e.g. "U.S. Gulf, USD/mt"
        if (isUsdBu) {
          rowComment = rowComment.replace(/USD\/mt/i, 'USD/bu');
        }

        const recordsRow = {
          type: 'data',
          Name: rowName,
          id: commodity,
        };

        const recordsCommentRow = {
          type: 'comment',
          Name: rowComment,
          id: commodity,
        };

        const compositePoint = dataRow?.points.reduce((recordPoint, point) => {
          const dataKeys = Object.keys(point).filter(key => point[key] !== null && point[key] !== undefined);
          dataKeys.forEach(key => recordPoint[key] = point[key]);
          return recordPoint;
        }, {}) || {};

        columns?.forEach(column => {
          // USD/BU prices are small (e.g. 8.72), keep cents
          recordsRow[column.field] = compositePoint[column.field]?.toFixed(isUsdBu ? 2 : 0) || '';
          recordsCommentRow[column.field] = '';
        });

        return [recordsRow, recordsCommentRow];
      }) || [];

      const periodCol: ColDef = { headerName: "", field: "Name", resizable: true, width: 240 };
      const result: TableProps = {
        columns: [periodCol].concat(columns),
        rows,
        getRowClass: param => {
          const commodityIndex = commodities?.indexOf(param.data.id) || 0;
          const evenClass = (commodityIndex + 1) % 2 === 0 ? 'event-row' : '';
          const typeClass = param.data.type === 'comment' ? 'comment-row' : 'data-row';
          return [typeClass, evenClass].filter(Boolean).join(' ');
        },
      }

      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, calendar, columns, units]);

  return state;
}
